<template>
  <div class="openLayersContainer" :class="{ whileDrawing: clickToDrawContour, whilePlacingMarker: clickToPlaceMarker }">
    <action-buttons
      v-if="!!map && showActionButtons"
      :map="map"
      :isScreenshotActive="clickToPlaceMarker"
      :isMoveActive="!!translateInteraction"
      :isPencilActive="clickToDrawContour"
      @clickScreenshotBtn="enableClickToPlaceMarker"
      @clickMoveBtn="toggleFeatureTranslateInteraction"
      @clickFloppyBtn="saveMapImage"
      @clickSaveBtn="openPrintToScaleModal"
      @clickPencilBtn="toggleDrawMode"
    ></action-buttons>
    <legend-handler
      v-if="!!map && showLegend"
      :isHidden="isLegendHandlerHidden"
      :legendEntries="legendEntries"
      @toggleHandler="toggleLegendHandler"
    ></legend-handler>
    <layer-handler
      v-if="!!map"
      :map="map"
      :editableMapLayers="!!editableMapLayers"
      :isHidden="isLayerHandlerHidden"
      :groupedEditableMapLayers="groupedEditableMapLayers"
      :expandedGroups="expandedGroups"
      :projectDataTree="projectDataTree"
      @toggleHandler="toggleLayerHandler"
      @toggleProjectNode="toggleProjectDataNode"
      @clickGroupArrow="collapseGroup"
      @expandGroup="expandGroup"
      @clickLayerArrow="collapseLayers"
      @clickLayerBox="setLayerVisible"
      @scrollOpacity="scrollOpacity"
      @clickOpacity="applyLayerConfiguration"
      @clickSpinner="changeOpacity"
      @clickSubLayerBox="setSubLayerVisible"
      @clickKMLVisible="changeVisibilityOfFeatures"
    ></layer-handler>
    <div v-if="showSearchBar" class="search-container">
      <search ref="search-component" style="width: 250px" :immediate="false" @search="search" />
    </div>
    <div id="ol-map" class="ol-map" @click="closeMapPanels"></div>

    <start-draw-modal :drawSource="drawSource" @startConfirmedDraw="toggleClickToDrawContour" @pasteConfirmedContour="addPastedContour"> </start-draw-modal>

    <context-menu
      v-if="!!selectedDrawFeature && showClickToContextMenu"
      :selectedDrawFeature="selectedDrawFeature"
      :clickToContextMenu="clickToContextMenu"
      :position="position"
      @clickOutside="clickToContextMenu = false"
      @clickEdit="startEditingContour"
      @clickDelete="deleteDrawFeature"
    ></context-menu>

    <contour-edit-panel
      v-if="editingContourName !== null"
      :contourName="editingContourName"
      :notice="contourEditNotice"
      @done="stopEditingContour"
    ></contour-edit-panel>
  </div>
</template>

<script>
// disable eslint check for new-cap because every openlayer object is lowercase
/* eslint-disable new-cap */

import { bus } from '@/bus.js'
import Vue from 'vue'
import $ from 'jquery'
import dateFormatter from '@/utils/dateFormatter'
import olMap from 'ol/Map'
import olView from 'ol/View'
import olLayerVector from 'ol/layer/Vector'
import olSourceVector from 'ol/source/Vector'

import olFormatKml from 'ol/format/KML'
import olStyleStyle from 'ol/style/Style'
import olStyleIcon from 'ol/style/Icon'
import WMTSCapabilities from 'ol/format/WMTSCapabilities'
import { MediaService } from '@/services/MediaWS/mediaService'

import * as olProj4 from 'ol/proj/proj4'
import proj4 from 'proj4'

import * as olControl from 'ol/control'
import * as olInteraction from 'ol/interaction'
import olInteractionSelect from 'ol/interaction/Select'
import olInteractionTranslate from 'ol/interaction/Translate'
import olGeomPoint from 'ol/geom/Point'
import * as olCondition from 'ol/events/condition'
import actionButtons from '@/components/openLayers/actionButtons'
import layerHandler from '@/components/openLayers/layerHandler'
import legendHandler from '@/components/openLayers/legendHandler'
import contextMenu from '@/components/openLayers/contextMenu'
import contourEditPanel from '@/components/openLayers/contourEditPanel'

import cookie from 'vue-cookie'
import config from '@/configurations/config.js'
import search from '@/components/_shared/search.vue'
import renderHandlers from '@/components/openLayers/renderHandlers'
import mapHandlers from '@/components/openLayers/mapHandlers'
import drawHandlers from '@/components/openLayers/drawHandlers'
import startDrawModal from '@/components/modal/startDrawModal.vue'

import klicUtils from '@/utils/klicUtil'
import Popup from '@/utils/popup'
import { localStorageVersions, MAP_LAYERS_CONFIGS_KEY, LOCAL_STORAGE_VERSIONS_KEY } from '../configurations/app/config-constants'
import projectionUtils from '@/utils/projectionUtil'
import mapUtils from '@/utils/mapUtils'
import sortUtils from '@/utils/sortUtils'
import stringUtils from '@/utils/stringUtils'
import { MeasurementPointService } from '@/services/DataWS/measurementPointService'
import { getProjectAddressQuery } from '@/services/addressSearch/addressSearchService'
import { zoomToAddress } from '@/utils/addressSearch/zoomToAddress'

// Origin URL = https://www.topotijdreis.nl/maps.json
import topoTijdReisJson from '@/configurations/app/config-map/topoTijdReis.json'
const mediaService = new MediaService()
const measurementPointService = new MeasurementPointService()

const parser = new WMTSCapabilities()

let popup = Popup[config.platform].default
let northArrowImg = new Image()
northArrowImg.src = window.location.protocol + '//' + window.location.host + config.root + 'assets/img/map_icons/north_arrow.png'

export default {
  name: 'openLayers',
  props: {
    MpIdFilter: {
      required: false
    },
    showSearchBar: {
      required: false,
      default: true
    },
    showActionButtons: {
      required: false,
      default: true
    },
    showLegend: {
      required: false,
      default: true
    },
    showClickToContextMenu: {
      required: false,
      default: true
    },
    enableAddFeatureSelectInteraction: {
      required: false,
      default: true
    }
  },
  data() {
    return {
      map: null,
      mapLayers: [],
      drawLayer: null,
      mapLayersConfig: {},
      expandedGroups: {},
      isLayerHandlerHidden: true,
      isLegendHandlerHidden: true,
      measurementPointTypeCodelist: [],
      measurementPointsReady: false,
      // The map opens on the project address. Until it is known whether that can be found, the
      // selected measurement point is only selected, not zoomed to.
      openingOnAddress: true,
      clickToPlaceMarker: false,
      clickToDrawContour: false,
      clickToContextMenu: false,
      selectedDrawFeature: null,
      // Only the name is reactive; the edited feature itself lives on editingDrawFeature, set in created.
      editingContourName: null,
      contourEditNotice: null,
      position: { x: 50, y: 50 },
      drawFeature: null,
      drawSource: null,
      drawMode: { type: 'location', location: '' },
      hoverMarker: false,
      hoveredMeasurementPointName: '',
      invertMapTextColour: true,
      rememberedKMLStyles: {},
      rememberedContourStyles: {},
      hiddenMeasurementPoints: [],
      hiddenContours: [],
      translateInteraction: null // If set allows drag and drop to move a measurementPoint. Set using toggleTranslateInteraction.
    }
  },
  components: {
    actionButtons,
    layerHandler,
    legendHandler,
    contextMenu,
    contourEditPanel,
    startDrawModal,
    search
  },
  computed: {
    PrGuid: {
      get() {
        return this.$store.state.projects.find((project) => {
          return project.PrID === this.$route.params.projectId
        }).PrGuid
      }
    },
    editableMapLayers: {
      get() {
        if (!this.map) {
          return null
        }
        let allLayers = this.map.getLayers().getArray()
        let val = allLayers.filter((layer) => layer.values_.editable === true)

        return val
      }
    },
    getSelectedMpId: {
      get() {
        if (this.$route.params.measurementPointId) return this.$route.params.measurementPointId
        if (this.$route.params.mpGuid) return this.$route.params.mpGuid
        return ''
      }
    },

    groupedEditableMapLayers: {
      get() {
        const arrayToObject = (array) =>
          array.reduce((obj, item) => {
            // if key already initialized, then add item to array, else initialize with array with item
            obj[item.values_.category] = obj[item.values_.category] ? [...obj[item.values_.category], item] : [item]
            return obj
          }, {})

        return arrayToObject(this.editableMapLayers)
      }
    },

    /**
     * The Project data tab as a tree. Its leaves are the features themselves, so a map can be put
     * together by switching single measurement points and sublocations off.
     */
    projectDataTree: {
      get() {
        if (!this.map) {
          return []
        }

        return [...this.measurementPointNodes(), ...this.contourNodes(), ...this.kmlNodes()]
      }
    },

    legendEntries: {
      get() {
        let measurementPoints = this.measurementPoints
        if (this.MpIdFilter) {
          measurementPoints = measurementPoints.filter((measurementPoint) => measurementPoint.MpID === this.MpIdFilter)
        }

        const mpTypeCodes = [...new Set(measurementPoints.map((measurementPoint) => measurementPoint.MpTypeCode).filter(Boolean))]

        return mpTypeCodes
          .map((mpTypeCode) => ({
            mpTypeCode,
            iconSrc: mapUtils.getMarkerIconForMpTypeCode(mpTypeCode),
            description: this.getDescriptionForMpTypeCode(mpTypeCode)
          }))
          .sort((a, b) => sortUtils.naturalSort(a.description, b.description))
      }
    },
    measurementPoints: {
      get() {
        return this.$store.state.measurementPoints
      },
      set(value) {
        this.$store.commit('setMeasurementPoints', value)
      }
    },
    topLayer: {
      get() {
        if (!this.map) {
          return null
        }
        // if layer is visible and it determines the text colour
        let layers = this.map
          .getLayers()
          .getArray()
          .filter((layer) => layer.getVisible() && typeof layer.values_['invertMapTextColour'] === 'boolean')

        if (layers.length === 0) {
          return null
        }

        // object in which we keep track of the layer indices per zindex
        let layerIndicesPerZIndex = {}

        for (let i = 0; i < layers.length; i++) {
          let layer = layers[i]
          let zindex = layer.getZIndex() || 0
          let layerIndicesAtZIndex = layerIndicesPerZIndex[zindex] || []
          // append index to key z index in layerIndicesPerZIndex
          layerIndicesPerZIndex[zindex] = [i, ...layerIndicesAtZIndex]
        }

        // get layer with max index of max z index
        return layers[Math.max(...layerIndicesPerZIndex[Math.max(...Object.keys(layerIndicesPerZIndex))])]
      }
    }
  },
  watch: {
    getSelectedMpId: {
      handler: function () {
        if (this.measurementPointsReady) {
          this.redrawMeasurementPoints()
          if (!this.$route.query.keepmapposition) {
            // Zoom in on selected measurementpoint
            mapHandlers.focusOnMP(this.getSelectedMpId, this.measurementPoints, this.map)
          }
        }
      },
      immediate: true
    },
    measurementPoints: {
      handler: function () {
        if (this.map) {
          this.redrawMeasurementPoints()
          this.focusOnSelectedMP()
        }
      },
      immediate: true
    },
    topLayer: {
      handler: function () {
        let topLayer = this.topLayer

        // if toplayer is not null, then use text colour setting of top layer and redraw mps
        if (topLayer) {
          this.invertMapTextColour = topLayer.values_.invertMapTextColour
          // The drawn contours label themselves the same way, so they need the setting too.
          drawHandlers.setMapTextColourInverted(this.invertMapTextColour)
          this.redrawMeasurementPoints()
        }
      }
    }
  },

  async created() {
    this.editingDrawFeature = null
    this.modifyInteraction = null

    if (this.showLegend) {
      this.setMeasurementPointTypeCodelist()
    }

    // Set project en Sublocation in Store so that it can be used to draw contours
    try {
      await Promise.all([this.$store.dispatch('fetchTblSubLocations', { PrID: this.$route.params.projectId }), this.$store.dispatch('fetchProject', { PrID: this.$route.params.projectId })])
    } finally {
      this.zoomToProjectAddress()
    }
  },

  mounted() {
    this.loadLayersConfig()
    this.mountMap()
    this.addKmlLayers()
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.stopEditingContourOnEscape)
  },
  methods: {
    async setMeasurementPointTypeCodelist() {
      try {
        this.measurementPointTypeCodelist = await this.$store.dispatch('getCodeListByCategoryIDOrCode', { categoryId: 13 })
      } catch (error) {
        // The legend falls back to raw type codes; a missing code list must not break the map.
        console.error('Measurement point type code list could not be loaded', error)
      }
    },

    getDescriptionForMpTypeCode(mpTypeCode) {
      const code = this.measurementPointTypeCodelist.find((item) => item.GcCode === mpTypeCode)
      if (!code || !code.GcDescription) {
        return mpTypeCode
      }

      return stringUtils.capitalizeFirstLetter(code.GcDescription)
    },

    closeMapPanels() {
      this.isLayerHandlerHidden = true
      this.isLegendHandlerHidden = true
    },

    // The two panels sit side by side and would overlap once opened, so only one is ever open.
    toggleLayerHandler() {
      this.isLayerHandlerHidden = !this.isLayerHandlerHidden
      if (!this.isLayerHandlerHidden) {
        this.isLegendHandlerHidden = true
      }
    },

    toggleLegendHandler() {
      this.isLegendHandlerHidden = !this.isLegendHandlerHidden
      if (!this.isLegendHandlerHidden) {
        this.isLayerHandlerHidden = true
      }
    },

    mountMap() {
      let baseLayers = config.map.BASELAYERS.map((layer) => mapHandlers.convertBaseLayerToOLLayerObject(layer, this.mapLayersConfig))
      // Kept in config order, because that order is the drawing order: an opaque WMTS background
      // has to stay below the WMS overlays listed after it.
      const mapLayers = config.map.WMSLAYERS(cookie.get('licensenumber')).map((layer) =>
        layer.type === 'WMTS'
          ? mapHandlers.convertToOLWmtsLayerObject(layer, this.mapLayersConfig[layer.name])
          : mapHandlers.convertToOLLayerObject(layer, this.mapLayersConfig[layer.name])
      )
      let companyLayers = config.map.companyLayers(cookie.get('licensenumber'), cookie.get('username')).map((layer) => mapHandlers.convertToOLLayerObject(layer, this.mapLayersConfig[layer.name]))

      const coordinateSystemRD = projectionUtils.coordinateSystems.find((item) => item.GcCode === 'RD')
      if (coordinateSystemRD) {
        proj4.defs(coordinateSystemRD.epsg, coordinateSystemRD.formula)
        olProj4.register(proj4)
      }

      // Sourceless until the user ticks one; see mapHandlers.createTopoTijdreisLayers.
      const topoLayers = mapHandlers.createTopoTijdreisLayers(topoTijdReisJson.mapLayers)

      this.mapLayers = [
        ...baseLayers,
        ...mapLayers,
        ...companyLayers,
        ...topoLayers,

        new olLayerVector(
          Object.assign(
            {
              category: 'ProjectData',
              name: 'map.layer.projectData.measurementPointLayer',
              opacity: 1,
              opacityPercentage: 100,
              editable: true,
              preload: Infinity,
              zIndex: 100,
              source: new olSourceVector()
            },
            this.mapLayersConfig['map.layer.projectData.measurementPointLayer']
          )
        )
      ]

      const categories = [...new Set(this.mapLayers.map((layer) => layer.values_.category))]
      categories.forEach((category) => this.registerCategory(category))

      drawHandlers.createDrawLayer(this)

      // Note that below a default config is given, but merged (overwritten) by that gotten from the localstorage
      this.map = new olMap({
        target: 'ol-map',
        layers: [...this.mapLayers, this.drawLayer],
        controls: olControl.defaults({ attribution: false }),
        interactions: olInteraction.defaults({
          altShiftDragRotate: false,
          pinchRotate: false,
          doubleClickZoom: false
        }),
        view: new olView({
          projection: 'EPSG:3857',
          center: [609215.06, 6699231.07],
          zoom: 6
        })
      })

      // Use the set methods to actually set the 'default' config. The redraw and the store that
      // applyLayerConfiguration does belong to an action, not to a layer, so they run once here.
      this.mapLayers.forEach((layer) => {
        this.writeLayerConfiguration(layer)
      })
      this.storeLayersConfig()
      if (this.enableAddFeatureSelectInteraction) {
        this.addFeatureSelectInteraction()
      }
      this.addClickInteraction()
      this.addPointerMoveInteraction()
      mapHandlers.addOverlayFeatures(this.map)
      drawHandlers.addDrawInteraction(this)
      mapHandlers.addContextMenuInteraction(this)
      this.redrawMeasurementPoints()
      this.focusOnSelectedMP()

      this.loadVisibleLazyLayers()
      drawHandlers.loadDrawFeatures(this).then(this.applyContourVisibility).catch((error) => {
        // Without them the map still works, it just has no contours on it.
        console.error('Contours could not be loaded', error)
      })
    },

    focusOnSelectedMP() {
      if (this.openingOnAddress) {
        mapHandlers.selectFeature('map.layer.projectData.measurementPointLayer', this.getSelectedMpId, this.map)
        return
      }
      mapHandlers.focusOnMP(this.getSelectedMpId, this.measurementPoints, this.map)
    },

    /**
     * Opens the map on the project address; without one that can be found, on the selected
     * measurement point as before. keepmapposition asks for the view to stay where it is.
     */
    async zoomToProjectAddress() {
      const query = this.$route.query.keepmapposition ? '' : getProjectAddressQuery(this.$store.state.currentProject)
      const selectedMpId = this.getSelectedMpId
      // Picking another measurement point while the address is looked up focuses that point instead.
      const found = !!this.map && await zoomToAddress(this.map.getView(), query, () => !!this.map && this.getSelectedMpId === selectedMpId)
      this.openingOnAddress = false
      if (!found && this.map) {
        this.focusOnSelectedMP()
      }
    },

    /**
     * Give a category a place in the layer panel. Categories can turn up after the map has been
     * built, and the panel reads this for every group it draws.
     */
    registerCategory(category) {
      if (this.expandedGroups[category]) {
        return
      }

      this.$set(this.expandedGroups, category, {
        isGroupExpanded: false,
        expandedLayers: {}
      })
    },

    /**
     * Put a layer on a map that is already running.
     */
    addMapLayer(layer) {
      this.registerCategory(layer.values_.category)
      this.map.addLayer(layer)
      this.applyLayerConfiguration(layer)
    },

    /**
     * The project KML layers come from the media service, which is too slow to hold the map up, so
     * they join it once they arrive.
     */
    async addKmlLayers() {
      let kmlLayers = []

      try {
        kmlLayers = await this.createKmlLayers()
      } catch (error) {
        // All the static map layers are still useful without them.
        console.error('KML map layers could not be loaded', error)
        return
      }

      kmlLayers.forEach((layer) => this.addMapLayer(layer))
    },

    /**
     * A layer that came back visible from localStorage still has to fetch its source before it
     * shows anything.
     */
    loadVisibleLazyLayers() {
      this.mapLayers
        .filter((layer) => layer.values_.visible && layer.values_.lazySource)
        .forEach((layer) => this.ensureLayerSource(layer))
    },

    deleteDrawFeature() {
      if (this.selectedDrawFeature === this.editingDrawFeature) this.stopEditingContour()
      drawHandlers.deleteDrawFeature(this)
    },

    startEditingContour() {
      drawHandlers.startEditingContour(this, this.selectedDrawFeature)
      window.addEventListener('keydown', this.stopEditingContourOnEscape)
    },

    stopEditingContour() {
      this.contourEditNotice = null
      drawHandlers.stopEditingContour(this)
      window.removeEventListener('keydown', this.stopEditingContourOnEscape)
    },

    removeEditedContourVertex(pixel) {
      const result = drawHandlers.removeEditedContourVertex(this, pixel)
      if (result === 'tooFewVertices') {
        const isLine = this.editingDrawFeature.getGeometry().getType() === 'LineString'
        this.contourEditNotice = this.$t(`map.DrawContour.ContourActions.editPanel.${isLine ? 'minimumLine' : 'minimumPolygon'}`)
      } else if (result === 'removed') {
        this.contourEditNotice = null
      }
    },

    stopEditingContourOnEscape(event) {
      if (event.key === 'Escape') this.stopEditingContour()
    },

    /**
     * Start Draw mode.
     */
    toggleDrawMode: function () {
      // Drawing a new contour for the sublocation being edited would remove the edited feature.
      this.stopEditingContour()
      if (this.clickToDrawContour) this.toggleClickToDrawContour(false, null)
      else bus.$emit('startDrawEvent')
    },

    /**
     * Toggle click to draw. If draw layer is not visible, enables it
     * @param clickToDrawContour sets value.
     * @param drawMode sets .
     * @obs draw mode is set by adding OL Draw and Modify interactions.
     */
    toggleClickToDrawContour: function (clickToDrawContour, drawMode) {
      this.drawMode = drawMode
      this.clickToDrawContour = clickToDrawContour

      if (this.clickToDrawContour) {
        this.map.addInteraction(this.drawFeature)
        this.drawLayer.values_.visible = true
      } else {
        this.map.removeInteraction(this.drawFeature)
      }
    },

    /**
     * Add a contour from a pasted WKT string to the draw layer and save it
     * @param pastedContour { type, location, wkt, epsg }
     */
    addPastedContour: function (pastedContour) {
      drawHandlers.addPastedContourToDraw(this, pastedContour)
      this.drawLayer.values_.visible = true
    },

    scrollOpacity(event) {
      event.preventDefault()
      let targetValue = parseInt(event.target.value) - parseInt(event.target.step) * Math.sign(event.deltaY)
      targetValue = Math.min(Math.max(targetValue, event.target.min), event.target.max)
      event.target.value = isNaN(targetValue) ? 0 : targetValue

      // Trigger an input event because calling applyLayerConfiguration directly is slower for some reason...
      let inputEvent = new Event('input', {
        bubbles: true,
        cancelable: true
      })
      event.target.dispatchEvent(inputEvent)
    },

    changeOpacity(mapLayer, increment, min, max) {
      // Don't have access to the input element here so we can't modify value and trigger input event. Will have to to the "slower" way.
      let targetValue = parseInt(mapLayer.values_.opacityPercentage) + increment
      targetValue = Math.min(Math.max(targetValue, min), max)
      mapLayer.values_.opacityPercentage = isNaN(targetValue) ? 0 : targetValue

      this.applyLayerConfiguration(mapLayer)
    },

    async createKmlLayers() {
      let kmlLayers = []
      let getLinkChain = []
      let fileNames = await this.getKmlLayers()

      fileNames.forEach((fileName) => {
        getLinkChain.push(mediaService.getProjectMediaLocationLink(this.PrGuid, 'MapLayers', fileName))
      })

      let getLinkResponses = await Promise.all(getLinkChain)
      getLinkResponses.forEach((getLinkResponse) => {
        if (getLinkResponse.status === 200 && getLinkResponse.data.ResultCode === 'GetMediaLink_Success') {
          let downloadUrl = new URL(getLinkResponse.data.Link) // The share link from dropbox isn't a direct link, but a preview link so we need to modify it.
          downloadUrl.host = 'dl.dropboxusercontent.com' // First we change the host, the default dropbox url doesn't have CORS enabled
          downloadUrl.searchParams.set('dl', '1') // Then we change the dl parameter to 1 which turns it into a direct download link instead of a preview.
          let kmlName = fileNames.shift().replace(/\.[^/.]+$/, '') // The layer name is the same as the filename withouth the extension.
          let kmlLayer = new olLayerVector(
            Object.assign(
              {
                category: 'ProjectData',
                name: kmlName,
                opacity: 1,
                opacityPercentage: 100,
                editable: true,
                preload: Infinity,
                source: new olSourceVector({
                  url: downloadUrl.href,
                  format: new olFormatKml(),
                  crossOrigin: 'anonymous'
                })
              },
              this.mapLayersConfig[kmlName]
            )
          )
          kmlLayer.type = 'kmlLayer'
          kmlLayers.push(kmlLayer)
        }
      })
      return kmlLayers
    },

    async getKmlLayers() {
      let fileNames = []
      let getMediaListResponse = await mediaService.getProjectMediaList(this.PrGuid, 'MapLayers')
      if (getMediaListResponse.status === 200 && getMediaListResponse.data.ResultCode === 'GetMediaList_Success') {
        getMediaListResponse.data.files.forEach((file) => {
          fileNames.push(file.FileName)
        })
      }
      return fileNames
    },

    /**
     * The interaction to allow selecting measurementPoints
     */
    addFeatureSelectInteraction() {
      let self = this
      let selectInteraction = new olInteractionSelect({
        layers: [mapHandlers.getLayerByName('map.layer.projectData.measurementPointLayer', this.map)],
        multi: false,
        condition: olCondition.click,
        hitTolerance: 5, // Add some margin to fill the vector shape
        style: null
      })

      this.map.addInteraction(selectInteraction)

      selectInteraction.on('select', function (event) {
        let feature = event.selected[0]
        // Both modes handle the click themselves in addClickInteraction; selecting on top of that
        // would navigate away from the measurementPoint that was just placed.
        if (self.clickToDrawContour || self.clickToPlaceMarker) return
        if (self.editingDrawFeature) return
        if (feature) {
          self.setFeatureTranslateInteraction(false)
          if (self.$listeners.selectRow) {
            self.$emit('selectRow', feature.getId())
            return
          }
          self.$router.push('/project/' + self.$route.params.projectId + '/measurementPoint/' + feature.getId() + '/map')
        } else {
          this.getFeatures().clear()
          if (self.getSelectedMpId) {
            feature = mapHandlers.getFeature(mapHandlers.getLayerByName('map.layer.projectData.measurementPointLayer', self.map), self.getSelectedMpId)
            if (feature) {
              this.getFeatures().push(feature)
            }
          }
        }
      })
    },

    /**
     * Toggle the feature translate interaction: toggles drag and drop for moving the current measurementPoint.
     */
    toggleFeatureTranslateInteraction() {
      if (this.translateInteraction) {
        this.map.removeInteraction(this.translateInteraction)
        this.translateInteraction = null
      } else {
        var self = this

        this.translateInteraction = new olInteractionTranslate({
          layers: [mapHandlers.getLayerByName('map.layer.projectData.measurementPointLayer', this.map)],
          features: mapHandlers.getInteraction(olInteractionSelect, this.map).getFeatures(),
          hitTolerance: 5 // Add some margin to fill the vector shape
        })

        this.map.addInteraction(this.translateInteraction)

        this.translateInteraction.on('translateend', function (event) {
          self.updateCoordinates(event.coordinate)
          // Disable the feature translation (also since updateCoordinates triggers a redraw which invalidates the feature object)
          self.setFeatureTranslateInteraction(false)
        })
      }

      // Redraw to change color
      this.redrawMeasurementPoints()
      mapHandlers.selectFeature('map.layer.projectData.measurementPointLayer', this.getSelectedMpId, this.map)
    },

    /**
     * Sets the feature translate interaction to be enabled or not.
     * Rather than toggling the current state, this only does something if the current state is not the desired state.
     */
    setFeatureTranslateInteraction(enable) {
      if (enable && !this.translateInteraction) {
        this.toggleFeatureTranslateInteraction()
      } else if (!enable && this.translateInteraction) {
        this.toggleFeatureTranslateInteraction()
      }
    },

    /**
     * The interaction when the cursor moves. Sets several values if hovered over a feature.
     */
    addPointerMoveInteraction() {
      let self = this
      this.map.on('pointermove', function (event) {
        // Disable standard cursor while drawing or placing: both modes own the cursor, and the
        // inline style written below would otherwise beat the stylesheet and take it back.
        if (self.clickToDrawContour || self.clickToPlaceMarker) return

        let feature = this.forEachFeatureAtPixel(
          event.pixel,
          function (feature, layer) {
            return feature
          },
          // Contours are filled, so without this the whole interior of a sublocation counts as a
          // hover target even though only measurementPoints are clickable here.
          { hitTolerance: 5, layerFilter: (layer) => layer !== self.drawLayer }
        ) // Add some margin to fill the vector shape

        if (feature && feature.getId() !== self.$route.params.measurementPointId) {
          this.getTargetElement().style.cursor = 'pointer'
          self.hoverMarker = true
        } else {
          this.getTargetElement().style.cursor = ''
          self.hoverMarker = false
        }
      })
    },

    /**
     * The function where all clicks on the map should be handled
     */
    addClickInteraction() {
      let self = this
      this.map.on('click', function (event) {
        // Disable standard clicks while drawing
        if (self.clickToDrawContour) return

        if (self.editingDrawFeature) {
          const isOnEditedContour = self.map.forEachFeatureAtPixel(event.pixel, (feature) => feature === self.editingDrawFeature, {
            hitTolerance: 10,
            layerFilter: (layer) => layer === self.drawLayer
          })
          if (!isOnEditedContour) self.stopEditingContour()
          return
        }

        // Places the measurementPoint on the clicked coordinates
        if (self.clickToPlaceMarker) {
          self.updateCoordinates(event.coordinate)
          self.enableClickToPlaceMarker()
          return
        }

        let klicLayer = mapHandlers.getLayerByName('Klic', self.map)

        // only trigger if klic layer is actually visible
        if (klicLayer && klicLayer.getVisible()) {
          self.handleKlicLayerEvent(event)
        }
      })
    },

    /**
     * Add layers of Klic notification on which the user has clicked.
     */
    handleKlicLayerEvent(event) {
      let klicLayer = mapHandlers.getLayerByName('Klic', this.map)
      if (!klicLayer) return

      // get url
      let url = klicLayer.getSource().getGetFeatureInfoUrl(event.coordinate, this.map.getView().getResolution(), this.map.getView().getProjection(), { INFO_FORMAT: 'application/json' })

      // id of the object in MongoDB with the specific layer information
      klicUtils.getKlicLayerObjID(url).then((layerObjID) => {
        // if a klic object has been found
        if (layerObjID) {
          // get all layers corresponding to klic with id layerObjID
          klicUtils.getKlicLayerInfo(layerObjID).then((sublayers) => {
            // Use the last 10 characters as an identifier (the whole string does not fit in the UI)
            let name = 'Klic_' + layerObjID.substr(-10)

            // if layer is not already added
            if (!mapHandlers.getLayerByName(name, this.map)) {
              sublayers = sublayers.map((id) => {
                return {
                  id: id,
                  title: id.substring(0, 50)
                }
              })

              let layer = mapHandlers.convertToOLLayerObject(
                {
                  name: name,
                  type: 'WMS',
                  country: 'ProjectData',
                  url: config.apis.mapLayerProxy + 'http://klic.terraindex.com:8080/geoserver/wms',
                  sublayers: sublayers
                },
                this.mapLayersConfig[name]
              )

              this.addMapLayer(layer)
            }
          })
        }
      })
    },

    /**
     * Updates the coordinates of the currently selected measurementPoint to the server
     */
    updateCoordinates(coordinate) {
      let self = this
      let geom = new olGeomPoint(coordinate)
      let feature = mapHandlers.getFeature(mapHandlers.getLayerByName('map.layer.projectData.measurementPointLayer', this.map), this.getSelectedMpId)

      if (feature) {
        feature.setGeometry(geom)
      }
      /* else {
         let marker = self.createMarker(geom, self.featureStyle, {id: self.$route.params.measurementPointId})
         self.addFeature('map.layer.projectData.measurementPointLayer', marker)
         } */

      let measurementPoint = this.measurementPoints.filter(function (mp) {
        return mp.MpGuid === self.$route.params.measurementPointId
      })[0]

      measurementPoint.MpPointGeometry = mapHandlers.writeWKTString(geom, mapHandlers.readWKTStringHeight(measurementPoint.MpPointGeometry))
      measurementPoint.MpDateLastChanged = dateFormatter.generateDateLastChangedValue()
      measurementPointService.updateMeasurementPoint(measurementPoint)

      self.revealMeasurementPointLayer()
      self.redrawMeasurementPoints()
    },

    /**
     * Placing a measurementPoint into a hidden layer moves it without showing anything, so the user
     * sees the coordinates change and nothing else. Asking to place one means wanting to see it.
     */
    revealMeasurementPointLayer() {
      const layer = mapHandlers.getLayerByName('map.layer.projectData.measurementPointLayer', this.map)
      if (!layer || layer.getVisible()) return

      layer.setVisible(true)
      this.writeLayerConfiguration(layer)
      this.storeLayersConfig()
    },

    /**
     * Enables or disabales the ability to place a marker
     */
    enableClickToPlaceMarker() {
      this.clickToPlaceMarker = !this.clickToPlaceMarker
    },

    /**
     * Redraws all measurementPoints when either the measurementPoints change or when the route changes.
     * Do note that this removes and re-adds all map features, meaning any previous references to features are invalidated
     */
    redrawMeasurementPoints: function (highlightSelectedMeasurementPoint = true) {
      let self = this
      if (this.measurementPoints.length === 0) {
        return
      }
      mapHandlers.removeAllFeatures('map.layer.projectData.measurementPointLayer', this.map)

      let filteredMeasurementPoints = this.measurementPoints

      if (this.MpIdFilter) {
        filteredMeasurementPoints = filteredMeasurementPoints.filter((measurementPoint) => measurementPoint.MpID === this.MpIdFilter)
      }

      filteredMeasurementPoints = filteredMeasurementPoints.filter((measurementPoint) => !this.hiddenMeasurementPoints.includes(measurementPoint.MpGuid))

      filteredMeasurementPoints.forEach(function (measurementPoint) {
        if (measurementPoint.MpPointGeometry) {
          let geometry
          try {
            // readWKTString throws on malformed WKT. The layer was cleared above, so letting that
            // escape would take every other measurementPoint down with it.
            geometry = mapHandlers.readWKTString(measurementPoint.MpPointGeometry)
          } catch (error) {
            console.error(`MeasurementPoint ${measurementPoint.MpGuid} has an unreadable geometry and is left off the map`, error)
            return
          }

          // Decide which svg icon to use for the current measurementPoint

          let svgStyle = mapUtils.getMarkerIconForMpTypeCode(measurementPoint.MpTypeCode)

          let featureStyle

          // Create the style for the current measurementPoint. Make sure it is red if its the current one.
          if (highlightSelectedMeasurementPoint && self.getSelectedMpId !== '' && self.getSelectedMpId === measurementPoint.MpGuid) {
            if (self.translateInteraction) {
              // Drag and drop to move the current measurementPoint is enabled
              featureStyle = new olStyleStyle({
                image: new olStyleIcon({
                  src: svgStyle,
                  scale: 0.5,
                  color: '#000000'
                })
              })
            } else {
              // MeasurementPoint is locked in place
              featureStyle = new olStyleStyle({
                image: new olStyleIcon({
                  src: svgStyle,
                  scale: 0.5,
                  color: '#ff0000'
                })
              })
            }
          } else {
            // MeasurementPoint is not currently selected
            featureStyle = new olStyleStyle({
              image: new olStyleIcon({
                src: svgStyle,
                scale: 0.5
              })
            })
          }

          // Add measurementPoint name to the style.
          renderHandlers.addTextToStyle(featureStyle, measurementPoint.MpName, self.invertMapTextColour)

          // Create and add the marker to the layer.
          let marker = mapHandlers.createMarker(geometry, featureStyle, {
            id: measurementPoint.MpGuid
          })
          mapHandlers.addFeature('map.layer.projectData.measurementPointLayer', marker, self.map)
        }
      })
      // Set to true so the watch on the route is activated.
      this.measurementPointsReady = true
    },

    measurementPointNodes() {
      const layer = mapHandlers.getLayerByName('map.layer.projectData.measurementPointLayer', this.map)

      if (!layer) {
        return []
      }

      let measurementPoints = this.measurementPoints.filter((measurementPoint) => measurementPoint.MpPointGeometry)

      if (this.MpIdFilter) {
        measurementPoints = measurementPoints.filter((measurementPoint) => measurementPoint.MpID === this.MpIdFilter)
      }

      if (measurementPoints.length === 0) {
        return []
      }

      const children = measurementPoints.map((measurementPoint) => ({
        key: measurementPoint.MpGuid,
        kind: 'measurementPoint',
        label: this.measurementPointLabel(measurementPoint),
        visible: !this.hiddenMeasurementPoints.includes(measurementPoint.MpGuid)
      }))

      return [
        {
          key: 'measurementPoints',
          kind: 'measurementPoints',
          label: this.$t('map.layer.projectData.measurementPointLayer'),
          // On when its points are actually drawn. The layer is switched per point like a contour is,
          // so the layer term only catches a stored configuration that still has it switched off.
          visible: layer.getVisible() && children.some((child) => child.visible),
          layer,
          children
        }
      ]
    },

    /**
     * The name with the type behind it, so two points of a different kind are told apart in the
     * list the way they are by their icon on the map.
     */
    measurementPointLabel(measurementPoint) {
      const description = this.getDescriptionForMpTypeCode(measurementPoint.MpTypeCode)

      // The code list is only fetched for the legend, and falls back to the raw code.
      if (!description || description === measurementPoint.MpTypeCode) {
        return measurementPoint.MpName
      }

      return `${measurementPoint.MpName} - ${description}`
    },

    contourNodes() {
      const contours = this.drawSource ? this.drawSource.getFeatures() : []
      const ofType = (type) => contours.filter((feature) => feature.values_.type === type)
      const isVisible = (feature) => !this.hiddenContours.includes(feature.values_.location)
      const nodes = []

      const projectContours = ofType('project')
      if (projectContours.length > 0) {
        nodes.push({
          key: 'projectContour',
          kind: 'contourGroup',
          contourType: 'project',
          label: this.$t('map.layer.projectData.project'),
          // The project contour and the sublocations sit in one draw layer, so they share its
          // opacity as well.
          layer: this.drawLayer,
          visible: projectContours.some(isVisible)
        })
      }

      const subLocationContours = ofType('subLocation')
      if (subLocationContours.length > 0) {
        nodes.push({
          key: 'subLocations',
          kind: 'contourGroup',
          contourType: 'subLocation',
          label: this.$t('map.layer.projectData.subLocation'),
          layer: this.drawLayer,
          visible: subLocationContours.some(isVisible),
          children: subLocationContours.map((feature) => ({
            key: feature.values_.location,
            kind: 'contour',
            label: feature.values_.name,
            visible: isVisible(feature)
          }))
        })
      }

      return nodes
    },

    kmlNodes() {
      const kmlLayers = this.map
        .getLayers()
        .getArray()
        .filter((layer) => layer.type === 'kmlLayer')

      if (kmlLayers.length === 0) {
        return []
      }

      return [
        {
          key: 'kml',
          kind: 'kmlGroup',
          label: this.$t('map.layer.projectData.kml'),
          visible: kmlLayers.some((layer) => layer.getVisible()),
          children: kmlLayers.map((layer) => ({
            key: layer.values_.name,
            kind: 'kmlLayer',
            label: layer.values_.name,
            layer,
            visible: layer.getVisible()
          }))
        }
      ]
    },

    /**
     * Switch a node of the Project data tree. A parent takes everything under it along.
     */
    toggleProjectDataNode(node) {
      switch (node.kind) {
        case 'measurementPoints':
          this.setMeasurementPointsHidden(node.children.map((child) => child.key), node.visible)
          break
        case 'kmlLayer':
          node.layer.setVisible(!node.visible)
          this.applyLayerConfiguration(node.layer)
          break
        case 'measurementPoint':
          this.setMeasurementPointsHidden([node.key], node.visible)
          break
        case 'contourGroup':
          this.setContoursHidden(this.contourLocations(node.contourType), node.visible)
          break
        case 'contour':
          this.setContoursHidden([node.key], node.visible)
          break
        case 'kmlGroup':
          this.setKmlLayersVisible(!node.visible)
          break
        default:
          console.error('Unknown project data node', node)
      }
    },

    contourLocations(contourType) {
      return this.drawSource
        .getFeatures()
        .filter((feature) => feature.values_.type === contourType)
        .map((feature) => feature.values_.location)
    },

    /**
     * A measurement point is switched per point, the way a contour is, so that the group and the
     * points under it cannot disagree about what is on the map.
     */
    setMeasurementPointsHidden(mpGuids, hide) {
      const hidden = this.hiddenMeasurementPoints.filter((guid) => !mpGuids.includes(guid))

      this.hiddenMeasurementPoints = hide ? hidden.concat(mpGuids) : hidden

      // A point switched back on needs a layer to appear in, and a configuration stored by an
      // earlier version of this panel can still have that layer switched off.
      if (!hide) {
        this.revealMeasurementPointLayer()
      }

      this.redrawMeasurementPoints()
    },

    /**
     * A contour is hidden by blanking its style, the way a KML feature is: the draw layer holds the
     * project contour and the sublocations together, so they cannot be switched as layers.
     */
    setContoursHidden(locations, hide) {
      const hidden = this.hiddenContours.filter((location) => !locations.includes(location))

      this.hiddenContours = hide ? hidden.concat(locations) : hidden
      this.applyContourVisibility()
    },

    applyContourVisibility() {
      if (!this.drawSource) {
        return
      }

      this.drawSource.getFeatures().forEach((feature) => {
        const location = feature.values_.location

        if (this.hiddenContours.includes(location)) {
          if (!this.rememberedContourStyles[location]) {
            this.rememberedContourStyles[location] = feature.getStyle()
          }

          feature.setStyle(new olStyleStyle({}))
        } else if (this.rememberedContourStyles[location]) {
          feature.setStyle(this.rememberedContourStyles[location])
        }
      })
    },

    setKmlLayersVisible(visible) {
      this.map
        .getLayers()
        .getArray()
        .filter((layer) => layer.type === 'kmlLayer')
        .forEach((layer) => {
          layer.setVisible(visible)
          this.applyLayerConfiguration(layer)
        })
    },

    /**
     * Updates the map layer visibility
     */
    setLayerVisible: async function (mapLayer) {
      let layer = mapHandlers.getLayerByName(mapLayer.values_.name, this.map)
      let subLayersVisible = !layer.state_.visible

      if (layer.values_.subLayers) {
        for (let prop in layer.values_.subLayers) {
          // skip loop if the property is from prototype
          if (!layer.values_.subLayers.hasOwnProperty(prop)) continue
          layer.values_.subLayers[prop]['visible'] = subLayersVisible
        }
      }

      if (mapLayer.values_.visible && !(await this.ensureLayerSource(layer))) {
        return
      }

      layer.setVisible(mapLayer.values_.visible)

      // Draw layer doesn't need to apply configuration.
      if (this.clickToDrawContour && !layer.values_.visible) {
        this.toggleClickToDrawContour(false, null)
      } else {
        this.applyLayerConfiguration(layer)
      }
      this.map.updateSize()
    },

    applyLayerConfiguration(layer) {
      this.writeLayerConfiguration(layer)

      this.redrawMeasurementPoints()

      this.storeLayersConfig()
    },

    /**
     * The part of the configuration that only touches the layer itself.
     */
    writeLayerConfiguration(layer) {
      layer.values_.opacity = layer.values_.opacityPercentage / 100
      if (layer.values_.subLayers && layer.getSource()) {
        let subLayers = Object.keys(layer.values_.subLayers)
          .filter((subLayer) => layer.values_.subLayers[subLayer]['visible'] === true)
          .join(',')

        let params = {
          LAYERS: subLayers
        }

        if (layer.values_.viewparams) {
          params['viewparams'] = layer.values_.viewparams
        }

        if (layer.values_.CQL_FILTER) {
          params['CQL_FILTER'] = layer.values_.CQL_FILTER
        }

        layer.getSource().updateParams(params)
      }
    },

    /**
     * Update the map layer sublayers visibility
     */
    setSubLayerVisible: async function (mapLayer) {
      let layer = mapHandlers.getLayerByName(mapLayer.values_.name, this.map)

      if (!(await this.ensureLayerSource(layer))) {
        return
      }

      mapLayer.values_.visible = true

      layer.setVisible(mapLayer.values_.visible)

      this.applyLayerConfiguration(layer)
    },

    /**
     * Give a layer its source before it is asked to draw anything. Answers whether the layer can be
     * drawn; a layer whose source could not be fetched is left switched off.
     */
    async ensureLayerSource(layer) {
      if (!layer.values_.lazySource) {
        return true
      }

      try {
        await mapHandlers.loadLazySource(layer, parser)

        // The source is brand new, so the opacity and the chosen WMS sublayers still have to be
        // written onto it.
        this.writeLayerConfiguration(layer)

        return true
      } catch (error) {
        console.error(`Layer ${layer.values_.name} could not be loaded`, error)
        layer.values_.visible = false
        layer.setVisible(false)
        return false
      }
    },

    changeVisibilityOfFeatures(incomingObject) {
      incomingObject.featureIDs.forEach((id) => {
        const key = `${incomingObject.mapLayer.ol_uid}-${id}`
        const feature = incomingObject.mapLayer.getSource().getFeatureByUid(id)
        if (incomingObject.isVisible) {
          feature.setStyle(this.rememberedKMLStyles[key])
        } else {
          this.rememberedKMLStyles[key] = feature.getStyle()
          feature.setStyle(new olStyleStyle({}))
        }
      })
    },

    loadLayersConfig: function () {
      // Always return some object, hopefully filled!
      try {
        // parse versions dict in local storage else {}
        let currentVersions = JSON.parse(localStorage.getItem(LOCAL_STORAGE_VERSIONS_KEY) || '{}')

        // if version is current version, then load else do not load settings
        if (currentVersions[MAP_LAYERS_CONFIGS_KEY] && currentVersions[MAP_LAYERS_CONFIGS_KEY] === localStorageVersions[MAP_LAYERS_CONFIGS_KEY]) {
          this.mapLayersConfig = Object.assign({}, JSON.parse(localStorage.getItem(MAP_LAYERS_CONFIGS_KEY)))
        } else {
          this.mapLayersConfig = {}
        }
      } catch (e) {}
    },

    storeLayersConfig: function () {
      let layersConfigs = {}
      this.editableMapLayers.forEach((layer) => {
        layersConfigs[layer.values_.name] = this.pickLayerConfig(layer.values_)
      })

      localStorage.setItem(MAP_LAYERS_CONFIGS_KEY, JSON.stringify(layersConfigs))

      // parse versions dict in local storage or initialize
      let currentVersions = JSON.parse(localStorage.getItem(LOCAL_STORAGE_VERSIONS_KEY) || '{}')
      // update version in object
      let updatedVersions = JSON.stringify(
        Object.assign(currentVersions, {
          [MAP_LAYERS_CONFIGS_KEY]: localStorageVersions[MAP_LAYERS_CONFIGS_KEY]
        })
      )

      // update version in local storage
      localStorage.setItem(LOCAL_STORAGE_VERSIONS_KEY, updatedVersions)
    },

    pickLayerConfig: function (layer) {
      // Define what attributes to store here
      return (({ name, visible, opacity, opacityPercentage, subLayers }) => ({
        name,
        visible,
        opacity,
        opacityPercentage,
        subLayers
      }))(layer)
    },

    /* takes a screenshot of the map, draws things on it and sends it off to the server */
    saveMapImage: function () {
      // we do not want the selected measurement point to have a different styling than the others
      this.redrawMeasurementPoints(false)

      let self = this
      this.map.once('postrender', function (event) {
        // draw north facing arrow
        let ctx
        let targetCanvas

        targetCanvas = document.createElement('canvas') // Create a new canvas
        let size = self.map.getSize() // Get the map pixel size
        targetCanvas.width = size[0] // Set the width and height of the canvas
        targetCanvas.height = size[1]
        ctx = targetCanvas.getContext('2d')

        Array.prototype.forEach.call(document.querySelectorAll('.ol-layer canvas'), function (canvas) {
          if (canvas.width > 0) {
            var opacity = canvas.parentNode.style.opacity
            ctx.globalAlpha = opacity === '' ? 1 : Number(opacity)
            var transform = canvas.style.transform
            // Get the transform parameters from the style's transform matrix
            var matrix = transform
              .match(/^matrix\(([^(]*)\)$/)[1]
              .split(',')
              .map(Number)
            // Apply the transform to the export map context
            CanvasRenderingContext2D.prototype.setTransform.apply(ctx, matrix)
            ctx.drawImage(canvas, 0, 0)
          }
        })

        let imgScale = 0.4
        ctx.setTransform(1, 0, 0, 1, 0, 0)

        // draw north arrow
        ctx.drawImage(
          northArrowImg,
          targetCanvas.width - northArrowImg.width * imgScale - 10, // 10 pixels from right
          10, // 10 pixels from top
          northArrowImg.width * imgScale,
          northArrowImg.height * imgScale
        )
        // draw scale
        let olScale = $('.ol-scale-line-inner')
        renderHandlers.writeScaleToCanvas(targetCanvas, ctx, olScale)

        // after drawing store image
        let blob
        try {
          blob = targetCanvas.toDataURL('image/png')
        } catch (e) {
          console.error(e)
          console.error('CORS! this probably indicates a cors problem, make sure the WMS server returns the right headers or use a proxy.')
          popup.popup(Vue.t('map.MapImageError'))
          self.map.renderSync()
          return false
        } finally {
          // highlight selected measurement point again
          self.redrawMeasurementPoints()
        }
        bus.$emit('saveMapImageEvent', blob)
        // redraw old canvas so there is no more trash on it
        self.map.renderSync()
        // finally show popup and pass it the blob
      })
      this.map.renderSync()
    },

    collapseGroup: function (key) {
      this.expandedGroups[key].isGroupExpanded = !this.expandedGroups[key].isGroupExpanded
    },
    expandGroup: function (key) {
      if (this.expandedGroups[key]) {
        this.expandedGroups[key].isGroupExpanded = true
      }
    },
    collapseLayers: function (groupKey, key) {
      const isExpanded = !this.expandedGroups[groupKey].expandedLayers[key]
      this.$set(this.expandedGroups[groupKey].expandedLayers, [key], isExpanded)
    },

    openPrintToScaleModal: function () {
      bus.$emit('printToScaleEvent', this.map)
    },

    search: function (query) {
      if (query) {
        mapHandlers.search(query, this.map)
      }
    }
  }
}
</script>

<style lang="less">
.openLayersContainer {
  float: left;
  width: calc(100% - 50px);
  height: 100%;
}

.whileDrawing {
  cursor: none;
}

.ol-map {
  height: 100%;
  background-color: #515151;
}

.glyphicon {
  display: inline-block;
  padding: 6px 8px;
}

// Placing a marker is about hitting an exact spot, so the crosshair is the cursor itself rather
// than an overlay chasing it. The glyph carries a black halo under a white body for the same
// reason renderHandlers.mapTextColours does: achromatic, so it reads on any background layer.
// Needs to outrank .ol-grab, which OpenLayers puts on the viewport whenever panning is available.
.whilePlacingMarker {
  #ol-map,
  #ol-map .ol-grab {
    cursor: url('./openLayers/placeMarker.svg') 12 12, crosshair;
  }
}

#ol-map {
  .ol-scale-line {
    right: 8px;
    left: auto;
    background: rgba(131, 131, 131, 1);
  }
}

.search-container {
  position: absolute;
  top: 17px;
  left: 90px;
  z-index: 1;
}
@import './openLayers/ol-custom.less';
</style>

<link rel="stylesheet" href="../node_modules/ol/ol.css">
